import {Button, CardActions, CardHeader, Grid, Popover, Tooltip, Typography} from '@mui/material'
import type {BaseSyntheticEvent, FC, ReactElement} from 'react'
import {StrictMode, useState} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import DateTimeDisplay from '@shared/components/DateTimeDisplay'
import SingleDateTimeSelector from '@shared/components/timerange/SingleDateTimeSelector'
import {
  type TimeIntervalContextProps,
  useTimeIntervalContext,
} from '@shared/providers/TimeIntervalProvider'

const CustomDatePicker: FC = (): ReactElement => {
  const [timeSelector, setTimeSelector] = useState<'start' | 'end'>('start')
  const [t] = useCustomTranslation('line_signals')
  const [t2] = useCustomTranslation('common')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [currentStartTime, setCurrentStartTime] = useState<Date | null>(null)
  const [currentEndTime, setCurrentEndTime] = useState<Date | null>(null)

  const {timeRange, startTime, endTime, updateStartTime, updateEndTime} =
    useTimeIntervalContext() as TimeIntervalContextProps

  const handleClick = (event: BaseSyntheticEvent): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const handleAccept = (): void => {
    if (currentStartTime) {
      updateStartTime(currentStartTime)
    }
    if (currentEndTime) {
      updateEndTime(currentEndTime)
    }
    handleClose()
  }

  const changeSelector = (event: BaseSyntheticEvent | string): void => {
    // First, check if event is a string and handle the logic for string input
    if (typeof event === 'string') {
      if (event.includes('start')) {
        setTimeSelector('start')
      } else if (event.includes('end')) {
        setTimeSelector('end')
      }
    } else if (typeof event === 'object' && event.target && typeof event.target.id === 'string') {
      // Make sure event is an object and event.target and event.target.id are not undefined
      if (event.target.id.includes('start')) {
        setTimeSelector('start')
      } else if (event.target.id.includes('end')) {
        setTimeSelector('end')
      }
    }
  }

  const handleStartDateChange = (newDate: Date | null): void => {
    if (newDate !== null && (newDate as Date).toString() !== 'Invalid Date') {
      setCurrentStartTime(newDate)
      // Don't automatically switch focus - let user control when to move to end time
    }
  }

  const handleEndDateChange = (newDate: Date | null): void => {
    if (newDate !== null && (newDate as Date).toString() !== 'Invalid Date') {
      setCurrentEndTime(newDate)
      // Don't automatically switch focus - let user control when to move to start time
    }
  }

  return (
    <StrictMode>
      <Tooltip
        arrow
        title={timeRange === 'null' ? '' : t('custom_time_selector_tooltip')}
        disableHoverListener={timeRange === 'null'}
        disableFocusListener={timeRange === 'null'}
        disableTouchListener={timeRange === 'null'}
      >
        <Button
          id="signal-screen-open-custom-timerange-selector"
          data-testid="mbai-signal-screen-custom-date-picker-button"
          data-cy="mbai-signal-screen-custom-date-picker-button"
          aria-describedby={id}
          variant="outlined"
          onClick={(e) => {
            if (timeRange === 'null') {
              handleClick(e)
            }
          }}
          color={timeRange === 'null' ? 'primary' : 'secondary'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textTransform: 'none',
          }}
        >
          <DateTimeDisplay
            data-name="date-picker-start-datetime"
            data-value={startTime?.toISOString()}
            showTime
            showYear
            date={startTime || ''}
            variant="body2"
          />
          <Typography variant="body2" sx={{mx: 1}}>
            -
          </Typography>
          <DateTimeDisplay
            data-name="date-picker-end-datetime"
            data-value={endTime?.toISOString()}
            showYear
            showTime
            date={endTime || ''}
            variant="body2"
          />
        </Button>
      </Tooltip>
      <Popover
        id={id}
        data-testid="mbai-signal-screen-custom-date-picker-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <CardHeader
          sx={{backgroundColor: 'background.paper'}}
          title={
            <Grid container spacing={1}>
              <Grid size={{xs: 12, sm: 6}}>
                <SingleDateTimeSelector
                  id="signal-start-date-time-selector"
                  label={t2('t_start')}
                  timeSelector="start"
                  onDateChange={handleStartDateChange}
                  defaultValue={currentStartTime || startTime}
                  focused={timeSelector === 'start'}
                  onFocusChanged={changeSelector}
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <SingleDateTimeSelector
                  id="signal-end-date-time-selector"
                  label={t2('t_end')}
                  timeSelector="end"
                  onDateChange={handleEndDateChange}
                  defaultValue={currentEndTime || endTime}
                  focused={timeSelector === 'end'}
                  onFocusChanged={changeSelector}
                />
              </Grid>
            </Grid>
          }
        />
        <CardActions sx={{backgroundColor: 'background.paper'}}>
          <Button
            // autoFocus
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAccept} sx={{ml: 1}}>
            Apply
          </Button>
        </CardActions>
      </Popover>
    </StrictMode>
  )
}
export default CustomDatePicker
